import React, { useState, useRef } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Drawer,
  Fab,
  Box,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  ChevronRight as ChevronRightIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import { useWizardCompletion } from '../hooks/useWizardCompletion';

interface ChecklistItem {
  label: string;
  sectionId: string;
}

interface MainStep {
  label: string;
  tab: string;
  checklistItems: ChecklistItem[];
}

interface WizardSidebarProps {
  activeMainStep: number;
  onMainStepChange: (step: number) => void;
}

const MAIN_STEPS: MainStep[] = [
  {
    label: 'Upload Images',
    tab: 'upload',
    checklistItems: [
      { label: 'Add Your Images', sectionId: 'upload-images' },
      { label: 'Choose Your Main Image', sectionId: 'upload-cover' }
    ]
  },
  {
    label: 'Describe Artwork',
    tab: 'describe',
    checklistItems: [
      { label: 'Creators', sectionId: 'describe-creators' },
      { label: 'Title & Location', sectionId: 'describe-title' },
      { label: 'Signature', sectionId: 'describe-signature' },
      { label: 'Creation Date', sectionId: 'describe-creation-date' }
    ]
  },
  {
    label: 'Media',
    tab: 'media',
    checklistItems: [
      { label: 'Getty Work Type', sectionId: 'media-work-type' },
      { label: 'Getty Techniques', sectionId: 'media-techniques' },
      { label: 'Getty Materials', sectionId: 'media-materials' },
      { label: 'Getty Substrate', sectionId: 'media-substrate' }
    ]
  },
  {
    label: 'Measurements',
    tab: 'measure',
    checklistItems: [
      { label: 'Measurements', sectionId: 'measure-dimensions' }
    ]
  },
  {
    label: 'Inventory & Albums',
    tab: 'catalogKeywords',
    checklistItems: [
      { label: 'Inventory Number', sectionId: 'catalog-number' },
      { label: 'Keywords', sectionId: 'catalog-keywords' },
      { label: 'Albums', sectionId: 'catalog-collections' }
    ]
  },
  {
    label: 'Provenance & References',
    tab: 'provenance',
    checklistItems: [
      { label: 'Provenance', sectionId: 'provenance-records' },
      { label: 'Exhibitions', sectionId: 'provenance-exhibitions' },
      { label: 'Publications', sectionId: 'provenance-publications' },
      { label: 'Awards & Honors', sectionId: 'provenance-awards' },
      { label: 'Related Works', sectionId: 'provenance-related-works' }
    ]
  },
  {
    label: 'Additional Information',
    tab: 'additionalInfo',
    checklistItems: [
      { label: 'Notes', sectionId: 'additional-notes-public' },
      { label: 'Location and Value', sectionId: 'additional-location-value' }
    ]
  },
  {
    label: 'Review & Finish',
    tab: 'review',
    checklistItems: []
  }
];

export const WizardSidebar: React.FC<WizardSidebarProps> = ({
  activeMainStep,
  onMainStepChange
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Local state for expanded/collapsed - allows toggling independent of active tab
  const [expandedStep, setExpandedStep] = useState<number | null>(activeMainStep);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getTabProgress, isStepComplete } = useWizardCompletion();
  const userCollapsedRef = useRef(false);

  // Sync expandedStep when activeMainStep changes from parent (e.g., programmatic navigation)
  // Skip auto-expansion when user has explicitly collapsed the section
  React.useEffect(() => {
    if (!userCollapsedRef.current) {
      setExpandedStep(activeMainStep);
    }
    userCollapsedRef.current = false;
  }, [activeMainStep]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });

      // Highlight briefly
      element.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 1000);
    }
  };

  const handleStepClick = (index: number) => {
    const isCurrentlyExpanded = expandedStep === index;
    if (isCurrentlyExpanded) {
      // Toggle collapse if clicking the already expanded step
      userCollapsedRef.current = true;
      setExpandedStep(null);
    } else {
      // Expand this step and navigate to its tab
      setExpandedStep(index);
      onMainStepChange(index);
      // Auto-close drawer on mobile after navigating
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const renderSidebarContent = () => (
    <List sx={{ px: 0 }}>
      {MAIN_STEPS.map((step, index) => {
        const isActive = activeMainStep === index;
        const isExpanded = expandedStep === index;
        const progress = getTabProgress(step.tab);

        return (
          <React.Fragment key={index}>
            {/* Main Step Item */}
            <ListItem
              component="button"
              selected={isActive}
              onClick={() => handleStepClick(index)}
              sx={{
                py: 1.5,
                px: 2,
                borderLeft: isActive
                  ? `4px solid ${theme.palette.primary.main}`
                  : '4px solid transparent',
                backgroundColor: isActive ? 'rgba(132, 22, 23, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(132, 22, 23, 0.12)'
                },
                transition: theme.transitions.create(['background-color', 'border-left'], {
                  duration: theme.transitions.duration.short
                }),
                cursor: 'pointer',
                border: 'none',
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ListItemText
                primary={step.label}
                primaryTypographyProps={{
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'primary.main' : 'text.primary'
                }}
              />
              {progress.total > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  {progress.completed}/{progress.total}
                </Typography>
              )}
              <ChevronRightIcon
                sx={{
                  color: isExpanded ? 'primary.main' : 'text.disabled',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create('transform', {
                    duration: theme.transitions.duration.short
                  })
                }}
              />
            </ListItem>

            {/* Nested Checklist (only for expanded step) */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 2 }}>
                {step.checklistItems.map((item, itemIndex) => {
                  const isComplete = isStepComplete(step.tab, itemIndex);

                  return (
                    <ListItem
                      component="button"
                      key={itemIndex}
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollToSection(item.sectionId);
                        if (isMobile) {
                          setMobileOpen(false);
                        }
                      }}
                      sx={{
                        py: 0.75,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)'
                        },
                        cursor: 'pointer',
                        border: 'none',
                        width: '100%',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {isComplete ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <RadioButtonUncheckedIcon
                            color="disabled"
                            fontSize="small"
                            data-testid="RadioButtonUncheckedIcon"
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: isComplete ? 'text.primary' : 'text.secondary'
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </React.Fragment>
        );
      })}
    </List>
  );

  const activeStepLabel = MAIN_STEPS[activeMainStep]?.label ?? 'Steps';

  return (
    <>
      {/* Mobile: FAB to open drawer */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="Open wizard steps"
          onClick={() => setMobileOpen(true)}
          variant="extended"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: theme.zIndex.speedDial,
            gap: 0.75
          }}
          size="medium"
        >
          <ListAltIcon />
          menu
        </Fab>
      )}

      {/* Mobile: Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box'
            }
          }}
        >
          <Box sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText
          }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Wizard Steps
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {activeStepLabel}
            </Typography>
          </Box>
          {renderSidebarContent()}
        </Drawer>
      )}

      {/* Desktop: Sticky sidebar */}
      {!isMobile && (
        <Paper
          sx={{
            width: 300,
            maxHeight: 'calc(100vh - 32px)',
            overflow: 'auto',
            position: 'sticky',
            top: 16,
            alignSelf: 'flex-start'
          }}
          elevation={1}
        >
          {renderSidebarContent()}
        </Paper>
      )}
    </>
  );
};
